// FIELD-BY-FIELD PREDICTION (Kevin, 2026-10-17: "you should be able to build a kickass prediction").
//
// The first prediction copied ONE past configuration whole, so every answer inherited that one
// job's quirks. Here every answer comes from the most reliable evidence that has one, and how
// reliable each kind of evidence is FOR EACH ANSWER is learned from history
// (scripts/predict-fieldwise/train.ts -> public/data/ndb/predict-model.json):
//
//   evidence   session      the customer's previous Save & Submit, within 2 h (the job's last line)
//              exact        the customer's earlier configuration with this same first page and
//                           model -- a repeat, copied whole
//              custModel    the customer's newest of this model (housing, size, burners, design)
//              custHousing  the customer's newest with this housing and burner count
//              custNewest   the customer's newest of any kind; custVote their last five, voted
//              pick         the server's whole-configuration pick (api/predict-config launch: the
//                           customer's configuration closest on housing and recency)
//              repNewest    the user's own newest for another customer; repModel the same model
//              everyModel / everyHousing / everyone   everybody's configurations, voted
//              (an "Old" suffix: that evidence is over a year old; a "~L" suffix: it is a legacy
//              order rather than a cpq2 configuration -- each learned separately. cpq2 has mostly
//              been the hard builds, so its history says less about an ordinary order's numbers
//              than the customer's legacy orders do, and vice versa.)
//
//   first page   each answer voted across the sources (log-odds of each source's reliability
//                for that answer, so agreeing sources add up). The job's own numbers (heat duty,
//                firetube size and run length, fuel pressure) only when the best source for them
//                is right at least half the time -- another job's numbers are almost never this
//                one's (firetube run length 0 of 93 for a new customer).
//   later pages  once the housing is chosen: an exact repeat copied whole; otherwise each answer
//                voted the same way (a section-from-one-source rule was tried and dropped: it
//                rewarded key count, not reliability).
//
// Held out (2026 orders and every cpq2 build, trained on 2025; `npm run predict:train`), against
// the whole-configuration prediction: ordinary orders later pages 73.8 -> 80.9%, 3 or fewer
// changes 37 -> 48%, median changes 11 -> 4; cpq2 builds page 1 64.2 -> 65.6%, later pages
// 62.8 -> 72.4%, median 27 -> 22, wrong job numbers shown 206 -> 151
// (CONFIG-PREDICTION-RESEARCH.md §13).
//
// Pure: no React, no fetch. The browser (state/AnswersContext, components/PredictLaunch,
// PredictionRefresh) and the training script run this same code.

import type { AnswerState } from "../types";

export type Flat = Record<string, string>;
export type Page = "p1" | "p2";

/** One past configuration as evidence, as the form would hold it (a legacy order completed by
 *  the operations first). */
export type Source = {
  id: string; name: string; kind: "cpq2" | "legacy"; createdAt: number; customerId: string; user: string;
  /** The configuration as the form would hold it. May be a lazy getter: a legacy order is
   *  completed by the operations only when it is actually used. */
  flat: Flat;
  /** The configuration as stored, enough to say its model and housing without completing it. */
  keys: Flat;
  /** a Save & Submit (or a legacy order), not a Favorite */
  auto?: boolean;
  /** a legacy QUOTE: its Configuration tab is often half filled, so its defaults are silence;
   *  a legacy sales order's answers are what was built. */
  quote?: boolean;
};

/** What training learned: reliability counts per page, answer and source, and everybody's votes. */
export type Model = {
  version: 1;
  trainedAt: string;
  /** `${page}\t${answerKey}\t${source}` -> [right, seen] */
  rel: Record<string, [number, number]>;
  everyone: { overall: Flat; byProduct: Record<string, Flat>; byHousing: Record<string, Flat> };
  /** Answer keys a legacy Configuration tab can carry at all. A legacy order is silent on any
   *  other (the operations fill them with defaults -- an aux UP of "None" that outvoted
   *  everyone's 2-1/2" on a third of the aux-UP misses, 2026-10-18). */
  legacyKeys?: string[];
};

export type Evidence = Record<string, Flat>;
/** The configuration behind each evidence source, where one configuration is (not a vote). */
export const evidenceNames = new WeakMap<Evidence, Record<string, string>>();

export const SESSION_MS = 2 * 60 * 60 * 1000;
const YEAR_MS = 365 * 86400000;
/** The job's own numbers: shown only when the best source for them is right this often. */
export const JOB_KEYS = new Set([
  "REQUIRED_HEAT_DUTY/REQUIRED_HEAT_DUTY", "FIRETUBE_DIAMETER/FIRETUBE_DIAMETER",
  "FIRETUBE_LENGTH/FIRETUBE_LENGTH", "FUEL_PRESSURE/FUEL_PRESSURE",
]);
/** NOTHING IS LEFT BLANK (Kevin, 2026-10-19: "byob housing row can be left blank but nothing else
 *  should be left blank ever"). The job's numbers used to show only when their best source was
 *  right half the time (JOB_MIN 0.5); now the best guess always shows, and the vote below falls
 *  back to what everyone orders for any answer no closer source carries. JOB_MIN is kept at 0
 *  for the training report, which still counts wrong job numbers. */
export const JOB_MIN = 0;
/** Answers the operations compute from the first page and keep right: never predicted over.
 *  Flange ID is the firetube size (op 347); a same-model order with another firetube put 16"
 *  over a correct 24" (2026-10-18). */
const COMPUTED = new Set(["FLANGE_ID"]);
/** A legacy order with fewer later-page answers than this recorded none: not evidence for them. */
export const THIN_KEYS = 8;
/** Tunables the training script may override for an experiment. */
export const settings: { exactLegacy: "any" | "none" | "model" } = { exactLegacy: "model" };
/** Never part of the repeat check: an old order rarely carries it, and a new job's run length
 *  does not change what the customer orders. */
const JOB_LENGTH = new Set(["FIRETUBE_LENGTH/FIRETUBE_LENGTH"]);
/** A source's starting reliability before evidence, worth five observations. */
export const PRIOR: Record<string, number> = {
  exact: 0.95, session: 0.85, pick: 0.6,
  custModel: 0.8, custModelOld: 0.7, custModelVote: 0.82, custHousing: 0.7, custHousingOld: 0.6,
  custNewest: 0.6, custNewestOld: 0.5, custVote: 0.62, repNewest: 0.45, repModel: 0.6,
  everyModel: 0.55, everyHousing: 0.45, everyone: 0.4,
  // The customer's own PO for this order (PO-PREDICTION.md): 93.8% right where it and the order
  // both state an answer, 45 POs from 45 customers. Per-answer counts come from po-reliability.json.
  po: 0.9,
  // The Flameco quote the PO cites: matched its order exactly on 6 of 12, partly on the rest.
  poQuote: 0.75,
  // The customer's earlier order the PO says this one repeats ("repeat of Po 70797").
  poRepeat: 0.9,
};
// Answers the operations compute or that belong to the housing choice: never evidence for the
// first page's own inputs, never part of the repeat comparison.
const NOT_INPUT = /^(BURNER_TYPE|CALCULATE2|SELECTION|ACTUAL_HEAT_FLUX|AIR_PRESSURE|FIRETUBE_SURFACE_AREA|REQUIRED_CELL_AREA|FUEL_DENSITY|HOUSING_VALIDATION|MODEL)(\/|$)/;
export const DERIVED = /^(DESCRIPTION|HIDDEN_ID_KEY|MAPPING|UNIQUE\d|MEMORY)/;
/** Free text that is this order's alone: a customer part number from another order would be
 *  CREATED as a record on submit, and notes describe one job. Never predicted, never voted. */
export const NEVER = /^(CUSTOMER_PART_NUMBER|INLET_CONNECTION_NOTES|COMPETITOR_NAME|DEBUG)(\/|$)/;
/** Table cells where "None" is the form's way of saying "not set": the UP and IN distances,
 *  the Inlet Housing Position (its picker offers Standard / Match Offset / Parallel, never
 *  None) and the Main and Pilot Orientation (those two pickers have no None either; an Aux
 *  cell's None is real: no inlet there). An open cell shows "None" as blank (TableWidget), and
 *  legacy orders completed by the operations say "None" on every one of them, which outvoted
 *  the customer's real numbers (Profire's main UP of 4, 2026-10-19). So "None" is silence
 *  there; a locked cell (no inlet) is forced to "None" by the form itself.
 *  Orientation joined on 2026-10-19: a Granite Peak line saved with its Main and Pilot
 *  orientation unset was copied whole as the exact repeat, and the next line's Main and Pilot
 *  cells came up blank while the customer's 63 orders all said 0 or 180. */
export const NONE_IS_SILENCE = /^(UP|IN|MATCH_OFFSET_POSITION)\/|^ORIENTATION_TABLE\/(MAIN|PILOT)_/;
const speaks = (k: string, v: string | undefined): v is string => v !== undefined && !(v === "None" && NONE_IS_SILENCE.test(k));

const qOf = (k: string) => (k.indexOf("/") < 0 ? k : k.slice(0, k.indexOf("/")));

// A memory shadow records the value its operation last set on a field; a field that differs
// from its shadow reads as the user's own edit and is no longer kept up to date
// (MIXERORIFICE_MEMORY: memory "mixer orifice override"). So a shadow always comes from the
// same source as its field: it belongs to the field's section.
const SHADOW_OF: Record<string, string> = {
  MIXERORIFICE_MEMORY: "MIXER_ORIFICE_SIZE", // op 348
  HINGE_SIZE_MEMORY: "HINGE_SIZE", // op 394
  LIFTING_LUGS_MEMORY: "LIFTING_LUGS", // op 900106
  MATCH_OFFSET_MEMORY: "MATCH_OFFSET_POSITION", // op 406
};

/** AnswerState -> { question (select: its first chosen option) | answer key (value) : text }.
 *  `optionOrder` makes a double-true select read as the operations read it (first option). */
export function flatten(answers: AnswerState, optionOrder?: Map<string, string[]>): Flat {
  const out: Flat = {};
  for (const [k, v] of Object.entries(answers || {})) {
    const i = k.indexOf("/");
    const q = i < 0 ? k : k.slice(0, i);
    if (v === true) { if (!(q in out)) out[q] = k.slice(i + 1); }
    else if (v === false || v === null || v === undefined || v === "") continue;
    else out[k] = String(v);
  }
  if (optionOrder) for (const [q, opts] of optionOrder) {
    if (opts.length < 2 || !(q in out)) continue;
    const first = opts.find((o) => answers[`${q}/${o}`] === true);
    if (first) out[q] = first;
  }
  return out;
}

/** Flat -> AnswerState: a select sets its option true and the others null. */
export function toAnswers(f: Flat, optionsOf: (question: string) => string[]): AnswerState {
  const a: AnswerState = {};
  for (const [k, v] of Object.entries(f)) {
    if (k.includes("/")) a[k] = v;
    else for (const o of optionsOf(k)) a[`${k}/${o}`] = o === v ? true : null;
  }
  return a;
}

/** The selected housing row's cell OD (the hand-entered row on a replacement / BYOB is row 1). */
export const cellOdOf = (f: Flat) => {
  const sel = f["SELECTION/SELECTION"] || "1";
  return String(f[`BURNER_TYPE/CELL_OD_${sel}`] || f["CELL_OD"] || "").replace(/[^0-9.]/g, "");
};
/** The model: housing type, height, cell OD, burners, design -- OT-FAH14/12-6 and OT-FAH20/12-10
 *  are different models (OGI, 2026-10-18: without the OD they voted together). */
export const productKey = (f: Flat) => [f["HOUSING/HOUSING"], f["DIMENSIONS/DIMENSIONS"], cellOdOf(f), f["BURNER_QTY"], f["DESIGN/DESIGN"]].map((x) => x || "").join("|");
export const housingKey = (f: Flat) => [f["HOUSING/HOUSING"], f["BURNER_QTY"]].map((x) => x || "").join("|");

/** Each answer most of these configurations share (at least `min` of them, and two). */
export function vote(fs: Flat[], min = 0.4): Flat {
  const counts = new Map<string, Map<string, number>>();
  for (const f of fs) for (const [k, v] of Object.entries(f)) {
    if (!speaks(k, v)) continue;
    if (!counts.has(k)) counts.set(k, new Map());
    counts.get(k)!.set(v, (counts.get(k)!.get(v) || 0) + 1);
  }
  const out: Flat = {};
  for (const [k, m] of counts) {
    let bv = "", bn = 0;
    for (const [v, n] of m) if (n > bn) { bv = v; bn = n; }
    if (bn >= Math.max(2, fs.length * min)) out[k] = bv;
  }
  return out;
}


export type EvidenceContext = {
  customerId: string;
  user: string;
  now: number;
  /** Everybody's votes: overall (page 1), for this model and housing (later pages). */
  everyone?: { overall?: Flat; product?: Flat; housing?: Flat };
  /** Is this answer key on the first page? */
  onFirstPage: (key: string) => boolean;
  /** The server's launch pick (first page only uses it). */
  launchPick?: Source;
  /** Section mode (util/sectionMode.ts): the answers the rep has confirmed. A customer's or
   *  rep's configuration that disagrees with them on the answers both hold is not evidence
   *  for the sections still to come: Clamped confirmed, only the Clamped orders speak. The
   *  aggregate votes (everybody's) are kept as they are. */
  conditions?: Flat;
  /** The blank form completed by the operations. A legacy QUOTE is evidence for a later-page
   *  answer only where it differs from this: its Configuration tab was often half filled, and
   *  the operations fill the rest with the same defaults the blank form has -- "no aux inlet",
   *  "no hood", "no union" -- which is silence, not an answer. A same-job quote so completed
   *  was copied whole as a repeat on a third of the replayed orders (2026-10-18). A legacy
   *  sales order's answers are what was built, defaults included. */
  blank?: Flat;
  /** Model.legacyKeys as a set: a legacy order speaks only on these. */
  legacyKeys?: ReadonlySet<string>;
};
const evidenceFlat = new WeakMap<Source, Flat>();
/** A source's later-page evidence: recorded answers and values that differ from the blank form. */
function laterOf(s: Source, ctx: EvidenceContext): Flat {
  if (s.kind !== "legacy" || (!ctx.legacyKeys && !ctx.blank)) return s.flat;
  let f = evidenceFlat.get(s);
  if (!f) {
    f = {};
    const full = s.flat;
    for (const [k, v] of Object.entries(full)) {
      if (ctx.onFirstPage(k)) { f[k] = v; continue; }
      if (ctx.legacyKeys && !ctx.legacyKeys.has(k)) continue;
      // A quote's blank-form default is silence; a sales order's counts, recorded or not (an
      // aux inlet it never mentions is an aux inlet it did not have -- measured 2026-10-18:
      // treating those as silence cost ordinary orders 1.7 points).
      if (s.quote && ctx.blank && v === (ctx.blank[k] ?? "")) continue;
      f[k] = v;
    }
    evidenceFlat.set(s, f);
  }
  return f;
}

/**
 * The evidence for one page. `current` is the form as it stands (for the later pages: the
 * user's first page with the chosen housing) -- it decides the model, the housing and the
 * repeat. Sources from the last two hours other than the newest Save & Submit are left out
 * (the same session saving the same configuration is not history).
 */
export function evidenceFor(page: Page, sources: Source[], ctx: EvidenceContext, current: Flat = {}): Evidence {
  const ev: Evidence = {};
  const names: Record<string, string> = {};
  evidenceNames.set(ev, names);
  const mineAll = sources.filter((s) => s.customerId === ctx.customerId && s.createdAt < ctx.now).sort((a, b) => b.createdAt - a.createdAt);
  const session = mineAll.find((s) => s.kind === "cpq2" && s.auto !== false && ctx.now - s.createdAt <= SESSION_MS);
  const mineP1 = mineAll.filter((s) => ctx.now - s.createdAt >= SESSION_MS);
  const others = sources.filter((s) => s.customerId !== ctx.customerId && s.createdAt < ctx.now).sort((a, b) => b.createdAt - a.createdAt);
  const old = (s: Source) => (ctx.now - s.createdAt < YEAR_MS ? "" : "Old");
  const kind = (s: Source) => (s.kind === "legacy" ? "~L" : "");
  if (session) { ev.session = session.flat; names.session = session.name; }
  if (mineP1[0]) { const k0 = "custNewest" + old(mineP1[0]) + kind(mineP1[0]); ev[k0] = mineP1[0].flat; names[k0] = mineP1[0].name; }
  if (mineP1.length >= 3) ev.custVote = vote(mineP1.slice(0, 5).map((s) => s.flat));
  const byUser = ctx.user ? others.filter((s) => s.user === ctx.user) : [];
  if (page === "p1") {
    if (ctx.launchPick) { const k0 = "pick" + kind(ctx.launchPick); ev[k0] = ctx.launchPick.flat; names[k0] = ctx.launchPick.name; }
    if (byUser[0]) { const k0 = "repNewest" + kind(byUser[0]); ev[k0] = byUser[0].flat; names[k0] = byUser[0].name; }
    if (ctx.everyone?.overall) ev.everyone = ctx.everyone.overall;
    // THE ORDER TYPE IS THE CUSTOMER'S (Kevin, 2026-10-18: "What? For a customer with only legacy
    // history, order type now follows the user's own last configuration"). Only this customer's
    // own cpq2 configurations say it: a legacy order has no order type (every translation reads
    // BYOB), and another customer's job -- the user's last, everybody's -- says nothing about
    // this one's. With none, it is not predicted and the form keeps its default for the rep.
    const own = new Set<Flat>();
    if (session) own.add(session.flat);
    for (const s of mineP1) if (s.kind === "cpq2") { own.add(s.flat); break; }
    if (ctx.launchPick && ctx.launchPick.kind === "cpq2" && ctx.launchPick.customerId === ctx.customerId) own.add(ctx.launchPick.flat);
    for (const [src, f] of Object.entries(ev)) {
      if (own.has(f) || !("ORDER_TYPE" in f)) continue;
      const { ORDER_TYPE: _drop, ...rest } = f;
      void _drop;
      ev[src] = rest;
    }
    return ev;
  }
  // A thin order says nothing about the later pages: a legacy quote whose Configuration tab was
  // never filled completes to "None" everywhere, and voted it predicted no aux inlets, no hood
  // and no union on a third of the replayed orders (2026-10-18). Only orders that recorded
  // the later pages are evidence for them.
  const thick = (s: Source) => s.kind === "cpq2" || Object.keys(s.keys).filter((k) => !ctx.onFirstPage(k)).length >= THIN_KEYS;
  const mine = mineAll.filter((s) => ctx.now - s.createdAt >= SESSION_MS && thick(s));
  const pk = productKey(current), hk = housingKey(current);
  const agrees = (s: Source) => {
    if (!ctx.conditions) return true;
    const f = s.flat;
    let both = 0, same = 0;
    for (const [k, v] of Object.entries(ctx.conditions)) { if (f[k] === undefined || v === "" || v === null) continue; both++; if (f[k] === String(v)) same++; }
    return both < 2 || same / both >= 0.5;
  };
  const mineAgreeing = mine.filter(agrees);
  delete ev.custVote;
  for (const k of Object.keys(ev)) if (k.startsWith("custNewest")) delete ev[k];
  if (mineAgreeing[0]) { const k0 = "custNewest" + old(mineAgreeing[0]) + kind(mineAgreeing[0]); ev[k0] = laterOf(mineAgreeing[0], ctx); names[k0] = mineAgreeing[0].name; }
  if (mineAgreeing.length >= 3) ev.custVote = vote(mineAgreeing.slice(0, 5).map((s) => laterOf(s, ctx)));
  // A REPEAT: the customer's earlier configuration of this model whose first page agrees with the
  // user's on every input it carries. Inputs the old order does not carry do not count: a legacy
  // order never recorded an order type (every translation reads BYOB) or a run length, and
  // demanding them is why Kevin's own 202412-544, re-entered to the letter, was not recognised
  // (2026-10-18). Then it is copied whole: a repeat is right on ~99% of answers.
  const sameModel = mineAgreeing.filter((s) => productKey(s.keys) === pk);
  const inputKey = (k: string) => ctx.onFirstPage(k) && !NOT_INPUT.test(k) && !DERIVED.test(k) && !JOB_LENGTH.has(k);
  // A legacy repeat counts only where this customer has no cpq2 configuration of the model
  // (settings.exactLegacy "model"): a hard build in cpq2 of the same model is a different order
  // from the ordinary one before it, and copying the old one whole was measured to cost 2.6 points
  // on cpq2 builds. "none" never copies a legacy repeat; "any" always does.
  const hasCpq2Model = mineAgreeing.some((s) => s.kind === "cpq2" && productKey(s.keys) === pk);
  const legacyOk = settings.exactLegacy === "any" || (settings.exactLegacy === "model" && !hasCpq2Model);
  const exact = sameModel.find((s) => {
    if (s.kind === "legacy" && !legacyOk) return false;
    const f = s.flat;
    // A legacy order never recorded its order type or production unit (the server fills the
    // unit in from someone's cpq2 configuration): neither can count against it.
    // ...and only inputs the user has filled in: a heat duty not yet typed is unknown, not a
    // mismatch (OGI's own cpq2 OT-FAH14/12-6-1 was refused for it, 2026-10-18).
    const ks = Object.keys(f).filter((k) => inputKey(k) && (current[k] || "") !== "" && !(s.kind === "legacy" && (k === "ORDER_TYPE" || k === "APPLICATION")));
    return ks.length >= 3 && ks.every((k) => current[k] === f[k]);
  });
  if (exact) { ev.exact = laterOf(exact, ctx); names.exact = exact.name; }
  if (sameModel[0]) { const k0 = "custModel" + old(sameModel[0]) + kind(sameModel[0]); ev[k0] = laterOf(sameModel[0], ctx); names[k0] = sameModel[0].name; }
  if (sameModel.length >= 3) ev.custModelVote = vote(sameModel.slice(0, 3).map((s) => laterOf(s, ctx)), 0.6);
  const sameHousing = mineAgreeing.filter((s) => housingKey(s.keys) === hk);
  if (sameHousing[0]) { const k0 = "custHousing" + old(sameHousing[0]) + kind(sameHousing[0]); ev[k0] = laterOf(sameHousing[0], ctx); names[k0] = sameHousing[0].name; }
  const userModel = byUser.filter(agrees).find((s) => productKey(s.keys) === pk);
  if (userModel) { const k0 = "repModel" + kind(userModel); ev[k0] = laterOf(userModel, ctx); names[k0] = userModel.name; }
  if (ctx.everyone?.product) ev.everyModel = ctx.everyone.product;
  if (ctx.everyone?.housing) ev.everyHousing = ctx.everyone.housing;
  // The last resort on the later pages too: what everyone orders, so no answer that anyone
  // has ever given is left blank (Kevin, 2026-10-19).
  if (ctx.everyone?.overall) ev.everyone = ctx.everyone.overall;
  return ev;
}

/** How often this source's value for this answer is right: learned counts plus the prior. */
export function reliability(rel: Model["rel"], page: Page, key: string, source: string, extra?: [number, number]): number {
  const c = rel[`${page}\t${key}\t${source}`] || [0, 0];
  const right = c[0] + (extra ? extra[0] : 0), seen = c[1] + (extra ? extra[1] : 0);
  return (right + 5 * (PRIOR[source.split("~")[0]] ?? 0.5)) / (seen + 5);
}

export type Choice = { values: Flat; from: Record<string, string>; /** the chosen source's reliability for that answer */ confidence: Record<string, number> };

/**
 * The predicted answers for a page. `onPage` says which answer keys belong to it. `relOf` may be
 * the training script's leave-one-customer-out counts. (`groupOf` is kept for callers; the
 * section rule it served was dropped, see below.)
 */
export function choose(
  page: Page,
  ev: Evidence,
  relOf: (key: string, source: string) => number,
  onPage: (key: string) => boolean,
  _groupOf?: (question: string) => string
): Choice {
  const values: Flat = {}, from: Record<string, string> = {}, confidence: Record<string, number> = {};
  // A repeat first, whole; the vote below fills what it does not carry (a legacy repeat says
  // nothing about the answers it left at the form's defaults).
  // The order the customer's PO says this one repeats comes first (it names the order; Profire's
  // PO-71241 "repeat of Po 70797", 2026-10-19: a same-model test order from that afternoon had
  // been copied instead, with an aux orientation of 45 where the named order said 90), then a
  // same-model repeat found by matching inputs.
  if (page === "p2") {
    for (const src of ["poRepeat", "exact"] as const) {
      const f = ev[src];
      if (!f) continue;
      for (const [k, v] of Object.entries(f)) if (!(k in values) && onPage(k) && !DERIVED.test(k) && !NEVER.test(k) && !COMPUTED.has(qOf(k)) && speaks(k, v)) { values[k] = v; from[k] = src; confidence[k] = relOf(k, src); }
    }
  }
  // A memory shadow is on no tab; it is in scope when the field it follows is.
  const inScope = (k: string) => onPage(k) || (SHADOW_OF[qOf(k)] !== undefined && onPage(`${SHADOW_OF[qOf(k)]}/_`));
  const keys = new Set<string>();
  for (const f of Object.values(ev)) for (const k of Object.keys(f)) if (inScope(k) && !DERIVED.test(k) && !NEVER.test(k) && !COMPUTED.has(qOf(k))) keys.add(k);
  // Every page: each answer voted across the sources (log-odds of each source's reliability for
  // that answer, so agreeing sources add up). A section rule -- the whole section from one
  // source -- was tried and dropped: scored by sum it rewarded the source with the most keys (a
  // full cpq2 snapshot over the customer's own completed order), scored by mean it punished a
  // source for carrying low-confidence defaults; the backtest found per-answer voting as
  // accurate, and Kevin's live test (2026-10-18) found it right where both section rules were
  // wrong. A memory shadow follows its field's source (SHADOW_OF), never voted on its own.
  const pick = (k: string): { v: string; src: string } | null => {
    const score = new Map<string, number>();
    const by = new Map<string, string>();
    let bestRel = 0;
    for (const [src, f] of Object.entries(ev)) {
      const v = f[k];
      if (!speaks(k, v)) continue;
      const r = Math.min(0.99, Math.max(0.01, relOf(k, src)));
      bestRel = Math.max(bestRel, r);
      score.set(v, (score.get(v) || 0) + Math.log(r / (1 - r)));
      if (!by.has(v) || r > relOf(k, by.get(v)!)) by.set(v, src);
    }
    if (page === "p1" && JOB_KEYS.has(k) && bestRel < JOB_MIN) return null; // JOB_MIN is 0: never
    let bv = "", bs = -Infinity;
    for (const [v, sc] of score) if (sc > bs) { bv = v; bs = sc; }
    return bs > -Infinity ? { v: bv, src: by.get(bv)! } : null;
  };
  for (const k of keys) {
    if (SHADOW_OF[qOf(k)] || k in values) continue;
    const c = pick(k);
    if (c) { values[k] = c.v; from[k] = c.src; confidence[k] = relOf(k, c.src); }
  }
  for (const k of keys) {
    const field = SHADOW_OF[qOf(k)];
    if (!field) continue;
    const owner = Object.keys(from).find((fk) => qOf(fk) === field);
    const src = owner ? from[owner] : null;
    if (src && ev[src][k] !== undefined) { values[k] = ev[src][k]; from[k] = src; confidence[k] = confidence[owner!] ?? 0.5; }
  }
  return { values, from, confidence };
}


// ---------------------------------------------------------------------------------------------
// NOTHING VISIBLE IS LEFT BLANK. Kevin, 2026-10-19: "no null values for visible fields with
// predictor on. this is a hard rule", and in the same breath "last resort needs to be an educated
// guess -- can't just throw in values all willy nilly".
//
// choose() answers from the evidence it has; a field no source speaks on, or one whose chosen
// value the form does not offer (an unset orientation copied from a repeat, 2026-10-19), was left
// blank. fillGaps() walks the form the way the rep sees it after the prediction -- the visible
// questions, the offered options, the unlocked cells -- and fills every blank, in this order:
//
//   1. gapVote     what everyone orders for this answer: this housing, then this model, then
//                  all orders (the model's plurality votes; "None" is still silence where the
//                  form has no None)
//   2. gapKin      a table cell: the cell beside it on THIS form (Pilot from Main, Aux 2 from
//                  Aux 1 ...), when that one is offered here
//   3. gapKinVote  a table cell: what everyone orders for the cell beside it
//   4. gapFirst    a select with no history at all: the first option the form offers, marked
//                  unsure so the rep looks at it. Free text has no guess (reported as a gap).
//
// Every fill carries its source, so the hover says where it came from, and the Save & Submit
// comparison records how often each kind was right (cpq2_prediction_outcomes.changed_detail).
// Pure, like the rest of this module: the browser and the replay run the same code.

import { evalRule, isHidden, type HideRuleInput } from "./rule";
import { getProfile } from "../products";
import type { Question } from "../types";

type ShapeOption = { code: string; hide: HideRuleInput };
type ShapeCell = { col: string; key: string; options: string[] | null; ronly: string };
export type FormShape = {
  selects: { q: string; hide: HideRuleInput; options: ShapeOption[] }[];
  texts: { q: string; key: string }[];
  tables: { q: string; cells: ShapeCell[] }[];
};

/** Which questions the gap filler never touches: computed text, the housing choice and what
 *  Generate computes (the rep's, util/prediction launchKeys keeps them off the page anyway), the
 *  job's own free text (NEVER), and the housing table itself. */
const NO_FILL = /^(DESCRIPTION|HIDDEN_ID_KEY|MAPPING|UNIQUE\d|MEMORY|BURNER_TYPE|CALCULATE2|SELECTION|HOUSING_VALIDATION|MODEL|DEBUG)/;

/** The form, enough to know a blank and what the field accepts. Built once per product. */
export function formShape(questions: Question[], product: string): FormShape {
  const profile = getProfile(product);
  const keep = (q: string, col: string): readonly string[] | null => {
    for (const r of profile.tableCellOptions ?? []) if (r.question === q && (!r.columns || r.columns.includes(col))) return r.keep;
    return null;
  };
  const shape: FormShape = { selects: [], texts: [], tables: [] };
  for (const q of questions) {
    const code = q.custrecord_cpqc_question_code;
    if (NO_FILL.test(code) || NEVER.test(code)) continue;
    const answers = [...(q._answers ?? [])].sort((a, b) => Number(a.custrecord_cpqc_answer_sequence || 0) - Number(b.custrecord_cpqc_answer_sequence || 0));
    const type = q.custrecord_cpqc_question_type;
    if (type === "4") {
      if (!answers.length) continue;
      shape.selects.push({
        q: code,
        hide: { hideRule: q.custrecord_cpqc_question_hide_rule, parsedHideRule: q.custrecord_cpqc_question_p_hide_rule },
        options: answers.map((a) => ({ code: a.custrecord_cpqc_answer_code, hide: { hideRule: a.custrecord_cpqc_answer_hide_rule, parsedHideRule: a.custrecord_cpqc_answer_parsed_hide_rule } })),
      });
    } else if (type === "7") {
      if (!answers.length) continue;
      shape.texts.push({ q: code, key: `${code}/${answers[0].custrecord_cpqc_answer_code}` });
    } else if (type === "11") {
      const cells: ShapeCell[] = [];
      for (const a of answers) {
        const col = a.custrecord_cpqc_answer_code;
        let options: string[] | null = null;
        try { const v = JSON.parse(String(a.custrecord_cpqc_answer_qc_opt || "")); if (Array.isArray(v) && v.length) options = v.map(String); } catch { /* free text cell */ }
        const k = keep(code, col);
        // A product's narrowing keeps the couplings that exist; "None" stays where the column has it.
        if (options && k) options = options.filter((o) => k.includes(o) || o === "None");
        cells.push({ col, key: `${code}/${col}_1`, options, ronly: String(a.custrecord_cpqc_answer_parsed_ronly_rule || "") });
      }
      if (cells.length) shape.tables.push({ q: code, cells });
    }
  }
  return shape;
}

const NO_VOTE = new Set(["ORDER_TYPE"]);
const blankValue = (v: unknown) => v === undefined || v === null || v === "" || v === false;
/** The column a cell is guessed from when nothing else speaks: Pilot from Main, Aux n from the
 *  Aux before it, Aux 1 from Main. */
const kinOf = (col: string): string | null => {
  if (col === "PILOT") return "MAIN";
  const m = /^AUX_(\d+)$/.exec(col);
  if (m) return Number(m[1]) > 1 ? `AUX_${Number(m[1]) - 1}` : "MAIN";
  return null;
};

export type GapFill = { key: string; how: "gapVote" | "gapKin" | "gapKinVote" | "gapFirst"; value: string; was?: string };

/**
 * Fill every visible blank on the page. `current` is the form as it stands (the rep's answers;
 * the blank form at launch); `visible` lists the question codes the rep can see for a state
 * (util/validation visibleQuestionCodes). `ev` is the page's evidence: its everyone votes are
 * the guesses. Returns the choice with the fills added and what was filled; `unfilled` is what
 * no educated guess exists for (free text with no history).
 */
export function fillGaps(
  _page: Page,
  c: Choice,
  shape: FormShape,
  current: AnswerState,
  ev: Evidence,
  ctx: { onPage: (k: string) => boolean; visible: (state: AnswerState) => ReadonlySet<string>; optionsOf: (q: string) => string[]; relOf: (k: string, src: string) => number },
): { c: Choice; filled: GapFill[]; unfilled: string[] } {
  const values: Flat = { ...c.values }, from = { ...c.from }, confidence = { ...c.confidence };
  const filled: GapFill[] = [];
  const unfilled = new Set<string>();
  const votes: Array<[string, Flat | undefined]> = [["everyHousing", ev.everyHousing], ["everyModel", ev.everyModel], ["everyone", ev.everyone]];
  const offeredOk = (v: string, offered: string[] | null) => !offered || offered.includes(v);
  // What everyone orders for `k`, if the form offers it here. Never the order type: every legacy
  // translation says BYOB, so everyone's vote for it is wrong by construction (the customer's
  // own cpq2 configurations decide it, or the form's first option, New Build, marked unsure).
  const voteFor = (k: string, offered: string[] | null): { v: string; src: string } | null => {
    if (NO_VOTE.has(qOf(k))) return null;
    for (const [src, f] of votes) { const v = f?.[k]; if (speaks(k, v) && offeredOk(v, offered)) return { v, src }; }
    return null;
  };
  const drop = (k: string) => { delete values[k]; delete from[k]; delete confidence[k]; };
  const set = (k: string, v: string, how: GapFill["how"], src: string, conf: number, was?: string) => {
    values[k] = v; from[k] = src; confidence[k] = conf; filled.push({ key: k, how, value: v, was });
  };
  for (let pass = 0; pass < 3; pass++) {
    const state: AnswerState = { ...current, ...toAnswers(values, ctx.optionsOf) };
    const vis = ctx.visible(state);
    const before = filled.length;
    for (const s of shape.selects) {
      if (!vis.has(s.q) || !ctx.onPage(`${s.q}/_`)) continue;
      const offered = s.options.filter((o) => !isHidden(o.hide, state)).map((o) => o.code);
      if (!offered.length) continue;
      const chosen = values[s.q] ?? s.options.find((o) => state[`${s.q}/${o.code}`] === true)?.code;
      if (chosen !== undefined && offered.includes(chosen)) continue;
      const was = chosen;
      if (chosen !== undefined && s.q in values) drop(s.q);
      else if (chosen !== undefined) continue; // the rep's own answer, hidden by a rule they set: theirs to change
      const v = voteFor(s.q, offered);
      if (v) set(s.q, v.v, "gapVote", "gapVote", ctx.relOf(s.q, v.src), was);
      else set(s.q, offered[0], "gapFirst", "gapFirst", 0.2, was);
    }
    for (const t of shape.texts) {
      if (!vis.has(t.q) || !ctx.onPage(t.key)) continue;
      const cur = values[t.key] ?? state[t.key];
      if (!blankValue(cur)) continue;
      const v = voteFor(t.key, null);
      if (v) set(t.key, v.v, "gapVote", "gapVote", ctx.relOf(t.key, v.src));
      else unfilled.add(t.key);
    }
    for (const t of shape.tables) {
      if (!vis.has(t.q) || !ctx.onPage(`${t.q}/_`)) continue;
      for (const cell of t.cells) {
        // A locked cell is the form's: it forces None there (TableWidget). Inlet Housing Position
        // Main locks only without a main inlet, whatever its rule says.
        const locked = t.q === "MATCH_OFFSET_POSITION" && cell.col === "MAIN"
          ? /^(|none)$/i.test(String(state["SIZE_TABLE/MAIN_1"] ?? "").trim())
          : !!cell.ronly && evalRule(cell.ronly, state);
        if (locked) continue;
        const cur = values[cell.key] ?? state[cell.key];
        const curText = blankValue(cur) ? "" : String(cur);
        const bad = curText !== "" && (!offeredOk(curText, cell.options) || (curText === "None" && NONE_IS_SILENCE.test(cell.key)));
        if (curText !== "" && !bad) continue;
        if (bad && !(cell.key in values)) continue; // the rep's own value
        const was = bad ? curText : undefined;
        if (bad) drop(cell.key);
        const v = voteFor(cell.key, cell.options);
        if (v) { set(cell.key, v.v, "gapVote", "gapVote", ctx.relOf(cell.key, v.src), was); continue; }
        const kin = kinOf(cell.col);
        const kinKey = kin ? `${t.q}/${kin}_1` : null;
        const kinVal = kinKey ? (values[kinKey] ?? state[kinKey]) : undefined;
        if (kinKey && kin && !blankValue(kinVal) && speaks(cell.key, String(kinVal)) && offeredOk(String(kinVal), cell.options)) { set(cell.key, String(kinVal), "gapKin", `gapKin:${kin}`, 0.35, was); continue; }
        const kv = kinKey ? voteFor(kinKey, cell.options) : null;
        if (kin && kv) { set(cell.key, kv.v, "gapKinVote", `gapKinVote:${kin}`, 0.3, was); continue; }
        if (cell.options?.length) { set(cell.key, cell.options[0] === "None" && NONE_IS_SILENCE.test(cell.key) ? cell.options[1] ?? cell.options[0] : cell.options[0], "gapFirst", "gapFirst", 0.2, was); continue; }
        unfilled.add(cell.key);
      }
    }
    if (filled.length === before) break;
  }
  return { c: { values, from, confidence }, filled, unfilled: [...unfilled] };
}
